import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, TrendingUp, Eye, EyeOff, Shield, X } from "lucide-react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import ForgotPassword from "@/components/ForgotPassword";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// EmailJS config
const EMAILJS_SERVICE_ID = "service_kvot05p";
const EMAILJS_TEMPLATE_ID = "template_r3x21la";
const EMAILJS_PUBLIC_KEY = "zZatKajcIyMeuEezi";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogleSignIn = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  const appName = encodeURIComponent('Compoundr');
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${appName}`;
};

const AuthModal = ({ isOpen, onClose, onSuccess, required = false }) => {
  // Support both prop naming conventions
  const open = isOpen;
  // Handle close - always allow X button to close, but prevent outside click if required
  const handleClose = () => {
    onClose?.();
  };
  // onOpenChange handles outside clicks - block if required
  const onOpenChange = (value) => {
    if (required && !value) return; // Prevent closing via outside click if required
    !value && onClose?.();
  };
  const onAuthSuccess = onSuccess;
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [signupData, setSignupData] = useState({
    email: "",
    password: "",
    phone: "",
  });
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  // Send notification email to admin
  const sendNotificationEmail = async (email, phone) => {
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          subscriber_email: email,
          subscriber_phone: phone || "Not provided",
          source: "signup",
          signup_time: new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
        },
        EMAILJS_PUBLIC_KEY
      );
      console.log("Notification email sent successfully");
    } catch (error) {
      console.error("Failed to send notification email:", error);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!signupData.email || !signupData.password) {
      toast.error("Please fill in email and password");
      return;
    }
    
    if (signupData.password.length < 5) {
      toast.error("Password must be at least 5 characters");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/signup`, {
        email: signupData.email,
        password: signupData.password,
        phone: signupData.phone || null,
      });
      
      // Save token and user data
      localStorage.setItem("user_token", res.data.token);
      localStorage.setItem("user_data", JSON.stringify(res.data.user));
      
      // Send notification email to admin
      sendNotificationEmail(signupData.email, signupData.phone);
      
      toast.success("Welcome to Compoundr! 🎉");
      onAuthSuccess(res.data.user);
      onOpenChange(false);
      
      // Stay on homepage - let user navigate to quiz from there
      // No automatic redirect to onboarding
      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (error) {
      if (error.response?.data?.detail === "Email already registered") {
        toast.error("This email is already registered. Please login.");
      } else {
        toast.error(error.response?.data?.detail || "Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error("Please fill in email and password");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, {
        email: loginData.email,
        password: loginData.password,
      });
      
      // Save token and user data based on "Remember me" preference
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("user_token", res.data.token);
      storage.setItem("user_data", JSON.stringify(res.data.user));
      
      // Also set in localStorage if rememberMe so it persists across sessions
      if (rememberMe) {
        localStorage.setItem("remember_me", "true");
      } else {
        localStorage.removeItem("remember_me");
        // Clear localStorage copies when not remembering
        localStorage.removeItem("user_token");
        localStorage.removeItem("user_data");
      }
      
      // Fetch user progress (including onboarding data) and restore to localStorage
      try {
        const userId = res.data.user.email || res.data.user.id;
        const progressRes = await axios.get(`${API_URL}/api/progress/${userId}`);
        const progress = progressRes.data;
        
        // Restore onboarding data to localStorage if completed
        if (progress.onboarding && progress.onboarding.investor_type) {
          localStorage.setItem("investorQuizCompleted", progress.onboarding.investor_type);
          localStorage.setItem("recommendedCategory", progress.onboarding.recommended_category);
          localStorage.setItem("onboardingComplete", "true");
        }
      } catch (progressError) {
        console.log("No progress data found for user");
      }
      
      toast.success("Welcome back! 👋");
      onAuthSuccess(res.data.user);
      onOpenChange(false);
      
      // Force page reload to ensure all components update with new auth state
      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal" hideCloseButton={true}>
        {/* Custom Close Button - Always works */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10"
          data-testid="auth-modal-close-btn"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
          <DialogTitle className="font-heading text-xl">
            Join Compoundr
          </DialogTitle>
          <DialogDescription>
            Create your free account to unlock all tools, quizzes, and personalized insights.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="signup" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
            <TabsTrigger value="login">Login</TabsTrigger>
          </TabsList>

          <TabsContent value="signup">
            <form onSubmit={handleSignup} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email Address *</Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="you@example.com"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  data-testid="signup-email-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">Password * (min 5 characters)</Label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    data-testid="signup-password-input"
                    required
                    minLength={5}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-phone">Phone Number (optional)</Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={signupData.phone}
                  onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                  data-testid="signup-phone-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Create Free Account"
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or continue with</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-300 hover:bg-slate-50"
                onClick={handleGoogleSignIn}
                data-testid="google-signup-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <p className="text-xs text-center text-slate-400">
                By signing up, you agree to our{" "}
                <a href="/privacy" className="text-emerald-600 hover:underline">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/terms" className="text-emerald-600 hover:underline">
                  Terms
                </a>
                .
              </p>
            </form>
          </TabsContent>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email Address</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  data-testid="login-email-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Option */}
              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="mt-0.5"
                  data-testid="remember-me-checkbox"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="remember-me" 
                    className="text-sm font-medium cursor-pointer"
                  >
                    Keep me logged in on this device
                  </Label>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Shield className="w-3 h-3 text-emerald-600" />
                    Your data is safe! We never share or sell your information.
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  "Login"
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">Or continue with</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full border-slate-300 hover:bg-slate-50"
                onClick={handleGoogleSignIn}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="w-full text-center text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </form>
          </TabsContent>
        </Tabs>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="absolute inset-0 bg-white rounded-lg z-10">
            <ForgotPassword
              onBack={() => setShowForgotPassword(false)}
              onSuccess={() => {
                // Keep the modal open to show success message
              }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
